//dependencies
import { isValid } from "date-fns";
import React, { useState } from "react";

import { Text, toast } from "@performyard/py-components";
//components
import { Avatar } from "@performyard/py-components";

import Button from "../../../../../components/Button";
import Container from "../../../../../components/Container";
import DatePicker from "../../../../../components/Inputs/DatePicker";
import FormModal from "../../../../../components/Modal/Templates/FormModal";
import Spacer from "../../../../../components/Spacer";

//hooks
import { useInternalApiUser, useMutateEmployeeActivation } from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";

//config
import Config from "../../../Config";
//css
import css from "./DeactivateUserModal.module.scss";

type DeactivateUserModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	userID: string;
};

const DeactivateUserModal = ({ isOpen, handleClose, userID }: DeactivateUserModalProps) => {
	const { handleQueryChange } = useSearchQueryParams({ schema: Config.viewPageSchema });

	const [terminationDate, setTerminationDate] = useState<Date | null>(new Date());
	const [validationError, setValidationError] = useState<string>("");

	const { data: user, isLoading } = useInternalApiUser(userID);

	const { mutate: deactivateUser, isPending } = useMutateEmployeeActivation({
		onSuccess: () => {
			handleClose();
			toast.success("User Deactivated");
		},
	});

	if (isLoading) return null;

	return (
		<FormModal
			isOpen={isOpen}
			handleClose={handleClose}
			buttons={[
				<Button key="deactivate-cancel" onClick={handleClose}>
					Cancel
				</Button>,
				<Button key="deactivate-submit" color="red" submit isRequesting={isPending}>
					Yes, Deactivate Employee
				</Button>,
			]}
			onSubmit={(event) => {
				event.preventDefault();
				if (!terminationDate || !isValid(terminationDate)) {
					setValidationError("Please select a termination date");
					return;
				} else {
					setValidationError("");
					const numberOfDirectReports = user?.directReports?.length || 0;
					if (numberOfDirectReports > 0) {
						handleQueryChange({ modal: "reassign", terminationDate: terminationDate });
					} else {
						deactivateUser({ employeeId: userID, activate: false, termination: terminationDate });
					}
				}
			}}
			contentClassName={css.deactivateUserModal}
		>
			<Avatar id={userID} size={120} name={user?.user?.name || "User"} />
			<Spacer height={2} />
			<Container gap="4px">
				<Text size="paragraph-2" weight="regular" color="neutral-10">
					Deactivate
				</Text>
				<Text size="paragraph-2" weight="semi-bold" color="neutral-10">
					{user?.name}
				</Text>
			</Container>
			<Spacer height={2} />
			<Container width={"288px"} horizontalAlignment="center">
				<DatePicker
					label="Termination Date"
					value={terminationDate || ""}
					onChange={(value) => setTerminationDate(value || null)}
					clearable
					hasIcon
					errorMessage={validationError}
					animatedError
					textInputClassName={css.datePicker}
				/>
			</Container>
		</FormModal>
	);
};

export default DeactivateUserModal;
